import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, ChevronDown, MapPin, Search } from 'lucide-react';
import { api } from '../services/api';
import {
  addDays,
  formatCurrency,
  formatDateKey,
  formatDateKeyFromMs,
  formatDisplayDate,
  formatDuration,
  formatTime,
} from '../features/workspace/utils/datetime';

const computeDepositAmount = (service, payment) => {
  if (!payment || payment.paymentCollection === 'manual') return 0;
  const price = Number(service?.price) || 0;
  if (price <= 0) return 0;

  if (payment.depositType === 'fixed') {
    const value = Math.max(0, Number(payment.depositValue) || 0);
    return Math.min(price, value);
  }

  const percent = Math.min(100, Math.max(0, Number(payment.depositValue) || 25));
  const minAmount = Math.max(0, Number(payment.depositMinAmount) || 50);
  const raw = Math.round(price * (percent / 100));
  return Math.min(price, Math.max(minAmount, raw));
};

/** Digits-only international number for https://wa.me/<number> */
const toWhatsAppNumber = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  return digits.length >= 8 ? digits : '';
};

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const locationQuery = (location) =>
  [location?.address, location?.city, location?.country].filter(Boolean).join(', ');

const googleMapsSearchUrl = (query) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

const googleMapsEmbedUrl = (query) =>
  `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=15&output=embed`;

const LocationAddressLink = ({ query, className }) => (
  <a
    href={googleMapsSearchUrl(query)}
    target="_blank"
    rel="noopener noreferrer"
    className={className}
  >
    <MapPin className="h-3.5 w-3.5 flex-none" />
    <span>{query}</span>
  </a>
);

const LocationMapCard = ({ name, query }) => {
  if (!name && !query) return null;

  return (
    <div className="overflow-hidden bg-sand-50 ring-1 ring-ink-900/10">
      {query ? (
        <div className="relative h-44 w-full overflow-hidden bg-ink-900/5 sm:h-52">
          <iframe
            title="Location on Google Maps"
            src={googleMapsEmbedUrl(query)}
            className="pointer-events-none h-[calc(100%+80px)] w-full -translate-y-10 border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <a
            href={googleMapsSearchUrl(query)}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute inset-0"
            aria-label={`Open ${query} in Google Maps`}
          />
        </div>
      ) : null}
      <div className="px-4 py-4 sm:px-5">
        {name ? (
          <p className="font-display text-xl font-light text-ink-900">{name}</p>
        ) : null}
        {query ? (
          <a
            href={googleMapsSearchUrl(query)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-start gap-2 text-sm text-ink-700/80 transition-colors hover:text-ink-900"
          >
            <MapPin className="mt-0.5 h-4 w-4 flex-none text-saffron-600" />
            <span>
              <span className="block underline-offset-2 hover:underline">{query}</span>
              <span className="mt-0.5 block text-xs text-ink-600/60">Open in Google Maps</span>
            </span>
          </a>
        ) : null}
      </div>
    </div>
  );
};

const StepChrome = ({ stepLabel, title, subtitle, onBack, backLabel = 'Back' }) => (
  <div className="mb-6">
    {onBack ? (
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-ink-700 transition-colors hover:text-ink-900"
      >
        <ArrowLeft className="h-4 w-4" />
        {backLabel}
      </button>
    ) : null}
    {stepLabel ? (
      <p className={`${onBack ? 'mt-5' : ''} text-xs uppercase tracking-[0.22em] text-saffron-600`}>
        {stepLabel}
      </p>
    ) : null}
    <h2 className={`${stepLabel || onBack ? 'mt-2' : ''} font-display text-3xl font-light`}>{title}</h2>
    {subtitle ? <p className="mt-2 text-sm text-ink-700/80">{subtitle}</p> : null}
  </div>
);

const PublicBooking = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [step, setStep] = useState('service');
  const [serviceId, setServiceId] = useState('');
  const [staffId, setStaffId] = useState('');
  const [date, setDate] = useState(formatDateKey());
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [details, setDetails] = useState({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [serviceQuery, setServiceQuery] = useState('');

  const selectedService = useMemo(
    () => payload?.services?.find((service) => service.id === serviceId) || null,
    [payload, serviceId]
  );

  const selectedStaff = useMemo(
    () => payload?.staff?.find((member) => member.id === staffId) || null,
    [payload, staffId]
  );

  const hasStaff = (payload?.staff?.length || 0) > 0;
  const paymentSettings = useMemo(() => {
    const location = payload?.location;
    return {
      paymentCollection: location?.paymentCollection === 'manual' ? 'manual' : 'online_deposit',
      depositRefundable: location?.depositRefundable !== false,
      depositType: location?.depositType === 'fixed' ? 'fixed' : 'percent',
      depositValue:
        location?.depositValue !== undefined && location?.depositValue !== null
          ? Number(location.depositValue)
          : 25,
      depositMinAmount:
        location?.depositMinAmount !== undefined && location?.depositMinAmount !== null
          ? Number(location.depositMinAmount)
          : 50,
    };
  }, [payload?.location]);

  const depositAmount = useMemo(
    () => computeDepositAmount(selectedService, paymentSettings),
    [selectedService, paymentSettings]
  );
  const requiresOnlineDeposit =
    paymentSettings.paymentCollection === 'online_deposit' && depositAmount > 0;

  const serviceGroups = useMemo(() => {
    const list = payload?.services || [];
    const cats = payload?.location?.serviceCategories || [];
    const byId = new Map(cats.map((cat) => [cat.id, { ...cat, services: [] }]));
    const uncategorized = [];
    list.forEach((service) => {
      if (service.categoryId && byId.has(service.categoryId)) {
        byId.get(service.categoryId).services.push(service);
      } else {
        uncategorized.push(service);
      }
    });
    const groups = cats
      .map((cat) => byId.get(cat.id))
      .filter((group) => group?.services?.length);
    if (uncategorized.length) {
      groups.push({ id: 'other', name: 'Other', services: uncategorized });
    }
    return groups;
  }, [payload]);

  const visibleServiceGroups = useMemo(() => {
    const query = serviceQuery.trim().toLowerCase();
    const groups = categoryFilter === 'all'
      ? serviceGroups
      : serviceGroups.filter((group) => group.id === categoryFilter);

    if (!query) return groups;
    return groups
      .map((group) => ({
        ...group,
        services: group.services.filter((service) => {
          const haystack = `${service.name} ${service.description || ''}`.toLowerCase();
          return haystack.includes(query);
        }),
      }))
      .filter((group) => group.services.length > 0);
  }, [serviceGroups, categoryFilter, serviceQuery]);

  const showCategoryNav = serviceGroups.length > 1;

  const goToStep = useCallback((next) => {
    setError('');
    setStep(next);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const loadPage = useCallback(async () => {
    setLoading(true);
    const result = await api.getPublicBooking(slug);
    if (result.error) {
      setError(result.error);
      setPayload(null);
    } else {
      setError('');
      setPayload(result.data);
    }
    setLoading(false);
  }, [slug]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment !== 'return') return;

    const transactionId = searchParams.get('transaction_id');
    const txRef = searchParams.get('tx_ref');
    const appointmentId = searchParams.get('appointment');
    const status = searchParams.get('status');

    let cancelled = false;
    (async () => {
      setVerifyingPayment(true);
      setError('');
      if (status && status !== 'successful' && status !== 'completed') {
        setError('Payment was not completed. You can try again from the deposit step.');
        setStep('deposit');
        setVerifyingPayment(false);
        setSearchParams({}, { replace: true });
        return;
      }

      const result = await api.verifyPublicBookingPayment(slug, {
        transactionId,
        txRef,
        appointmentId,
      });

      if (cancelled) return;

      if (result.error) {
        setError(result.error);
        setStep('deposit');
      } else {
        setConfirmation(result.data);
        setStep('done');
      }
      setVerifyingPayment(false);
      setSearchParams({}, { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, slug]);

  useEffect(() => {
    const name = payload?.business?.name;
    if (name) {
      document.title = `${name} | Book on Spirara`;
      return () => {
        document.title = 'Spirara | Booking for Spa and Beauty Businesses';
      };
    }
    return undefined;
  }, [payload?.business?.name]);

  useEffect(() => {
    if (step !== 'time' || !serviceId) return;

    let cancelled = false;
    (async () => {
      setSlotsLoading(true);
      setSelectedSlot(null);
      const result = await api.getPublicAvailability(slug, {
        serviceId,
        date,
        staffId: staffId || undefined,
      });
      if (!cancelled) {
        if (result.error) {
          setError(result.error);
          setSlots([]);
        } else {
          setError('');
          setSlots(result.data?.slots || []);
        }
        setSlotsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [step, slug, serviceId, date, staffId]);

  const selectService = (id) => {
    setServiceId(id);
    setSelectedSlot(null);
    if (hasStaff) {
      goToStep('staff');
    } else {
      setStaffId('');
      goToStep('time');
    }
  };

  const selectStaff = (id) => {
    setStaffId(id);
    setSelectedSlot(null);
    goToStep('time');
  };

  const submitBooking = async () => {
    if (!selectedSlot) return;

    setSubmitting(true);
    setError('');
    const result = await api.createPublicBooking(slug, {
      serviceId,
      staffId: staffId || null,
      startAt: selectedSlot.startAt,
      clientName: details.clientName.trim(),
      clientPhone: details.clientPhone.trim() || null,
      clientEmail: details.clientEmail.trim() || null,
      notes: details.notes.trim() || null,
    });
    setSubmitting(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    setConfirmation(result.data);
    goToStep('done');
  };

  const startFlutterwaveCheckout = async () => {
    if (!selectedSlot) return;
    if (!payload?.location?.payoutReady) {
      setError(
        'This business has not finished connecting payouts yet. Please try again later or pay at the salon.'
      );
      return;
    }

    setSubmitting(true);
    setError('');
    const result = await api.checkoutPublicBooking(slug, {
      serviceId,
      staffId: staffId || null,
      startAt: selectedSlot.startAt,
      clientName: details.clientName.trim(),
      clientPhone: details.clientPhone.trim() || null,
      clientEmail: details.clientEmail.trim() || null,
      notes: details.notes.trim() || null,
    });
    setSubmitting(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    if (result.data?.paymentLink) {
      window.location.href = result.data.paymentLink;
      return;
    }

    setError('Could not start payment. Please try again.');
  };

  const goToDeposit = (event) => {
    event.preventDefault();
    if (!selectedSlot) return;
    if (!details.clientPhone.trim()) {
      setError('Add a phone number so we can text you the booking details.');
      return;
    }
    if (requiresOnlineDeposit) {
      goToStep('deposit');
      return;
    }
    submitBooking();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await startFlutterwaveCheckout();
  };

  const stepMeta = useMemo(() => {
    const withStaff = hasStaff;
    const withDeposit = requiresOnlineDeposit;
    let cursor = 1;
    const numbers = { service: cursor++ };
    if (withStaff) numbers.staff = cursor++;
    numbers.time = cursor++;
    numbers.details = cursor++;
    if (withDeposit) numbers.deposit = cursor++;
    const total = cursor - 1;
    const n = numbers[step];
    if (!n) return null;
    return `Step ${n} of ${total}`;
  }, [hasStaff, requiresOnlineDeposit, step]);

  if (loading || verifyingPayment) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-sand-100">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-ink-900/20 border-t-saffron-500" />
        {verifyingPayment ? (
          <p className="text-sm text-ink-700/80">Confirming your payment…</p>
        ) : null}
      </div>
    );
  }

  if (!payload?.business) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sand-100 px-5 font-body text-ink-900">
        <div className="max-w-md text-center">
          <h1 className="font-display text-4xl font-light">Booking unavailable</h1>
          <p className="mt-3 text-ink-700/80">{error || 'This booking page could not be found.'}</p>
          <Link to="/" className="mt-8 inline-block text-sm font-semibold underline decoration-saffron-500 decoration-2 underline-offset-4">
            Go to Spirara
          </Link>
        </div>
      </div>
    );
  }

  const { business, location, services, staff } = payload;
  const locationLine = locationQuery(location);
  const coverImageUrl = business.coverImageUrl || null;
  const hasCover = Boolean(coverImageUrl);
  const whatsappNumber = toWhatsAppNumber(business.phone || location?.phone);
  const whatsappHref = whatsappNumber ? `https://wa.me/${whatsappNumber}` : null;
  const summaryBits = [
    selectedService?.name,
    selectedStaff?.name,
    step !== 'service' && step !== 'staff' && selectedSlot
      ? `${formatDisplayDate(date)} · ${selectedSlot.time}`
      : null,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-sand-100 font-body text-ink-900">
      {hasCover ? (
        <header className="relative isolate overflow-hidden bg-ink-900">
          <img
            src={coverImageUrl}
            alt={`${business.name} cover`}
            className="absolute inset-0 h-full w-full origin-center object-cover animate-kenburns"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-950/90 via-ink-950/40 to-ink-950/25" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-950/50 via-transparent to-transparent" />

          <div
            className={`relative mx-auto flex max-w-3xl flex-col justify-between px-5 sm:px-6 ${
              step === 'service'
                ? 'min-h-[13.5rem] pb-8 pt-5 sm:min-h-[17rem] sm:pb-10 sm:pt-6'
                : 'min-h-[7.5rem] pb-5 pt-5 sm:min-h-[8.5rem] sm:pt-6'
            }`}
          >
            <div className="flex justify-end">
              <Link
                to="/"
                className="text-sm text-sand-100/75 transition-colors hover:text-sand-50"
              >
                Spirara<span className="text-saffron-500">.</span>
              </Link>
            </div>

            <div className="max-w-xl">
              <p className="text-xs uppercase tracking-[0.18em] text-sand-200/65">Book with</p>
              <h1
                className={`mt-1 font-display font-light tracking-tight text-sand-50 ${
                  step === 'service'
                    ? 'text-3xl sm:text-4xl md:text-5xl'
                    : 'text-2xl sm:text-3xl'
                }`}
              >
                {business.name}
              </h1>
              {step !== 'service' && step !== 'done' && summaryBits.length > 0 && (
                <p className="mt-2 text-sm text-sand-100/75">{summaryBits.join(' · ')}</p>
              )}
            </div>
          </div>
        </header>
      ) : (
        <header className="border-b border-ink-900/10 bg-sand-50">
          <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-5 sm:px-6">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-ink-600/60">Book with</p>
              <h1 className="font-display text-2xl font-light tracking-tight sm:text-3xl">
                {business.name}
              </h1>
              {step !== 'service' && step !== 'done' && summaryBits.length > 0 && (
                <p className="mt-1 text-sm text-ink-700/70">{summaryBits.join(' · ')}</p>
              )}
            </div>
            <Link to="/" className="text-sm text-ink-700/70 hover:text-ink-900">
              Spirara<span className="text-saffron-500">.</span>
            </Link>
          </div>
        </header>
      )}

      <main
        className={`mx-auto max-w-3xl px-5 sm:px-6 ${
          hasCover ? 'py-7 sm:py-10' : 'py-8 sm:py-12'
        }`}
      >
        {error && step !== 'done' && (
          <div className="mb-6 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {step === 'service' && (
          <section>
            {(location?.name || locationLine) && (
              <LocationMapCard name={location?.name} query={locationLine} />
            )}
            <div className={(location?.name || locationLine) ? 'mt-10' : ''}>
              <StepChrome
                stepLabel={stepMeta}
                title="Choose a service"
                subtitle={showCategoryNav ? 'Pick a category, then the service you want.' : null}
              />
              {(showCategoryNav || (payload?.services?.length || 0) > 6) && (
                <div className={`mb-6 grid gap-3 ${showCategoryNav ? 'sm:grid-cols-2' : ''}`}>
                  {showCategoryNav && (
                    <label className="block">
                      <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                        Category
                      </span>
                      <span className="relative block">
                        <select
                          value={categoryFilter}
                          onChange={(e) => setCategoryFilter(e.target.value)}
                          className="w-full appearance-none bg-sand-50 py-3 pl-4 pr-10 text-sm text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                        >
                          <option value="all">All categories</option>
                          {serviceGroups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name} ({group.services.length})
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-600/70" />
                      </span>
                    </label>
                  )}
                  <label className="block">
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                      Search
                    </span>
                    <span className="relative block">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-600/50" />
                      <input
                        type="search"
                        value={serviceQuery}
                        onChange={(e) => setServiceQuery(e.target.value)}
                        placeholder="Filter services"
                        className="w-full bg-sand-50 py-3 pl-10 pr-4 text-sm text-ink-900 ring-1 ring-inset ring-ink-900/15 placeholder:text-ink-600/50 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                      />
                    </span>
                  </label>
                </div>
              )}
              {visibleServiceGroups.length === 0 ? (
                <p className="bg-sand-50 px-4 py-8 text-center text-sm text-ink-700/75 ring-1 ring-ink-900/10">
                  No services match that filter.
                </p>
              ) : (
              <div className="space-y-8">
                {visibleServiceGroups.map((group) => (
                  <div key={group.id}>
                    {showCategoryNav && categoryFilter === 'all' && (
                      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-saffron-600">
                        {group.name}
                      </p>
                    )}
                    <div className="space-y-2">
                      {group.services.map((service) => (
                        <button
                          key={service.id}
                          type="button"
                          onClick={() => selectService(service.id)}
                          className="group flex w-full items-center justify-between gap-4 bg-sand-50 px-4 py-4 text-left text-ink-900 ring-1 ring-ink-900/10 transition-colors hover:bg-ink-900 hover:text-sand-50"
                        >
                          <span>
                            <span className="block font-medium">{service.name}</span>
                            <span className="mt-1 block text-sm text-ink-600/70 group-hover:text-sand-300">
                              {formatDuration(service.durationMinutes)}
                              {service.description ? ` · ${service.description}` : ''}
                            </span>
                          </span>
                          <span className="flex shrink-0 items-center gap-2 text-sm font-medium">
                            {formatCurrency(service.price, service.currency)}
                            <ArrowRight className="h-4 w-4 opacity-40 transition-transform group-hover:translate-x-0.5 group-hover:opacity-100" />
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          </section>
        )}

        {step === 'staff' && (
          <section>
            <StepChrome
              stepLabel={stepMeta}
              title="Who would you like?"
              subtitle={selectedService?.name}
              onBack={() => goToStep('service')}
              backLabel="Change service"
            />
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => selectStaff('')}
                className="group flex w-full items-center justify-between gap-4 bg-sand-50 px-4 py-4 text-left text-ink-900 ring-1 ring-ink-900/10 transition-colors hover:bg-ink-900 hover:text-sand-50"
              >
                <span>
                  <span className="block font-medium">Anyone available</span>
                  <span className="mt-1 block text-sm text-ink-600/70 group-hover:text-sand-300">
                    First free stylist / therapist
                  </span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 opacity-40 transition-transform group-hover:translate-x-0.5 group-hover:opacity-100" />
              </button>
              {staff.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => selectStaff(member.id)}
                  className="group flex w-full items-center justify-between gap-4 bg-sand-50 px-4 py-4 text-left text-ink-900 ring-1 ring-ink-900/10 transition-colors hover:bg-ink-900 hover:text-sand-50"
                >
                  <span>
                    <span className="block font-medium">{member.name}</span>
                    {member.title ? (
                      <span className="mt-1 block text-sm text-ink-600/70 group-hover:text-sand-300">
                        {member.title}
                      </span>
                    ) : null}
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 opacity-40 transition-transform group-hover:translate-x-0.5 group-hover:opacity-100" />
                </button>
              ))}
            </div>
          </section>
        )}

        {step === 'time' && (
          <section>
            <StepChrome
              stepLabel={stepMeta}
              title="Pick a time"
              subtitle={[
                selectedService?.name,
                formatDuration(selectedService?.durationMinutes),
                selectedStaff?.name || (hasStaff ? 'Anyone available' : null),
              ]
                .filter(Boolean)
                .join(' · ')}
              onBack={() => goToStep(hasStaff ? 'staff' : 'service')}
              backLabel={hasStaff ? 'Change person' : 'Change service'}
            />

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setDate((prev) => addDays(prev, -1))}
                className="px-3 py-2 text-sm ring-1 ring-ink-900/15 hover:bg-white"
              >
                Prev
              </button>
              <input
                type="date"
                value={date}
                min={formatDateKey()}
                onChange={(e) => setDate(e.target.value)}
                className="bg-white px-3 py-2 text-sm ring-1 ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
              />
              <button
                type="button"
                onClick={() => setDate((prev) => addDays(prev, 1))}
                className="px-3 py-2 text-sm ring-1 ring-ink-900/15 hover:bg-white"
              >
                Next
              </button>
            </div>
            <p className="mt-3 text-sm text-ink-600/70">{formatDisplayDate(date)}</p>

            <div className="mt-6">
              {slotsLoading ? (
                <div className="flex justify-center py-10">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-900/20 border-t-saffron-500" />
                </div>
              ) : slots.length === 0 ? (
                <p className="text-sm text-ink-700/80">No open times on this day. Try another date.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {slots.map((slot) => {
                    const selected = selectedSlot?.startAt === slot.startAt;
                    return (
                      <button
                        key={slot.startAt}
                        type="button"
                        onClick={() => setSelectedSlot(slot)}
                        className={`px-2 py-3 text-sm ${
                          selected
                            ? 'bg-saffron-500 text-ink-900'
                            : 'bg-sand-50 text-ink-800 ring-1 ring-ink-900/10 hover:bg-ink-900 hover:text-sand-50'
                        }`}
                      >
                        {slot.time}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              type="button"
              disabled={!selectedSlot}
              onClick={() => goToStep('details')}
              className="group mt-8 inline-flex w-full items-center justify-center gap-2 bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50 sm:w-auto"
            >
              Continue
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </section>
        )}

        {step === 'details' && (
          <section>
            <StepChrome
              stepLabel={stepMeta}
              title="Your details"
              subtitle={`${selectedService?.name} · ${formatDisplayDate(date)} · ${selectedSlot?.time}`}
              onBack={() => goToStep('time')}
              backLabel="Change time"
            />

            <form className="space-y-4" onSubmit={goToDeposit}>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Full name
                </label>
                <input
                  required
                  value={details.clientName}
                  onChange={(e) => setDetails((prev) => ({ ...prev, clientName: e.target.value }))}
                  className="w-full bg-white px-4 py-3 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Phone <span className="text-saffron-600">*</span>
                </label>
                <input
                  required
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+233 24 000 0000"
                  value={details.clientPhone}
                  onChange={(e) => setDetails((prev) => ({ ...prev, clientPhone: e.target.value }))}
                  className="w-full bg-white px-4 py-3 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Email
                </label>
                <input
                  type="email"
                  value={details.clientEmail}
                  onChange={(e) => setDetails((prev) => ({ ...prev, clientEmail: e.target.value }))}
                  className="w-full bg-white px-4 py-3 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                />
              </div>
              <p className="text-xs text-ink-600/70">
                We&rsquo;ll text your booking confirmation to this number.
              </p>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Notes
                </label>
                <textarea
                  rows={2}
                  value={details.notes}
                  onChange={(e) => setDetails((prev) => ({ ...prev, notes: e.target.value }))}
                  className="w-full bg-white px-4 py-3 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="group inline-flex w-full items-center justify-center gap-2 bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50"
              >
                {submitting
                  ? 'Confirming…'
                  : requiresOnlineDeposit
                    ? 'Continue'
                    : 'Confirm booking'}
                {!submitting && (
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                )}
              </button>
            </form>
          </section>
        )}

        {step === 'deposit' && requiresOnlineDeposit && (
          <section>
            <StepChrome
              stepLabel={stepMeta}
              title="Pay deposit"
              subtitle={
                paymentSettings.depositRefundable
                  ? 'Secure your appointment. This deposit is refundable per the salon’s policy. Balance is due at the salon.'
                  : 'Secure your appointment. This deposit is non-refundable. Balance is due at the salon.'
              }
              onBack={() => goToStep('details')}
              backLabel="Edit details"
            />

            <div className="divide-y divide-ink-900/10 bg-sand-50 ring-1 ring-ink-900/10">
              <div className="flex justify-between gap-4 px-4 py-3 text-sm">
                <span className="text-ink-700/80">Service</span>
                <span className="font-medium text-ink-900">
                  {formatCurrency(selectedService?.price || 0, selectedService?.currency)}
                </span>
              </div>
              <div className="flex justify-between gap-4 px-4 py-3 text-sm">
                <span className="font-medium text-ink-900">Deposit due now</span>
                <span className="font-semibold text-ink-900">
                  {formatCurrency(depositAmount, selectedService?.currency)}
                </span>
              </div>
            </div>

            <p className="mt-3 text-xs text-ink-600/70">
              {paymentSettings.depositRefundable ? 'Refundable deposit' : 'Non-refundable deposit'}
              {paymentSettings.depositType === 'percent'
                ? ` · ${paymentSettings.depositValue}% of service`
                : ' · Fixed amount'}
            </p>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              {!payload?.location?.payoutReady ? (
                <p className="border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Online deposit is enabled, but this business has not connected a payout wallet yet.
                </p>
              ) : (
                <p className="text-sm text-ink-700/80">
                  You&rsquo;ll complete payment securely with Flutterwave (mobile money or card,
                  depending on your country).
                </p>
              )}
              <button
                type="submit"
                disabled={submitting || !payload?.location?.payoutReady}
                className="w-full bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50"
              >
                {submitting
                  ? 'Redirecting to payment…'
                  : `Pay ${formatCurrency(depositAmount, selectedService?.currency)} deposit`}
              </button>
            </form>
          </section>
        )}

        {step === 'done' && confirmation && (
          <section className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center bg-ink-900">
              <Check className="h-7 w-7 text-saffron-400" />
            </div>
            <h2 className="mt-6 font-display text-4xl font-light">Booking confirmed</h2>
            <p className="mx-auto mt-3 max-w-md text-ink-700/80">
              {confirmation.service?.name} with {confirmation.business?.name} on{' '}
              {formatDisplayDate(formatDateKeyFromMs(confirmation.appointment.startAt))} at{' '}
              {formatTime(confirmation.appointment.startAt)}.
            </p>
            {confirmation.appointment?.depositPaid ? (
              <p className="mx-auto mt-2 max-w-md text-sm text-ink-600/70">
                Deposit of{' '}
                {formatCurrency(
                  confirmation.appointment.depositAmount,
                  confirmation.appointment.currency
                )}{' '}
                received. Balance due at the salon.
              </p>
            ) : null}
            {confirmation.client?.phone ? (
              <p className="mx-auto mt-2 max-w-md text-sm text-ink-600/70">
                A confirmation SMS is on its way to {confirmation.client.phone}.
              </p>
            ) : null}
            {locationQuery(confirmation.location) ? (
              <LocationAddressLink
                query={locationQuery(confirmation.location)}
                className="mx-auto mt-5 inline-flex items-center gap-1.5 text-sm text-ink-700 underline-offset-2 hover:text-ink-900 hover:underline"
              />
            ) : null}
          </section>
        )}
      </main>

      {whatsappHref ? (
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Chat with ${business.name} on WhatsApp`}
          className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-ink-900/20 transition-transform hover:scale-105 hover:bg-[#1ebe57] focus:outline-none focus-visible:ring-2 focus-visible:ring-saffron-500 focus-visible:ring-offset-2 sm:bottom-6 sm:right-6"
        >
          <WhatsAppIcon className="h-7 w-7" />
        </a>
      ) : null}
    </div>
  );
};

export default PublicBooking;
